import { useQuery, useTranslation } from '@gimlite/common';
import { Main, useStoreUIV1 } from '@gimlite/ui-v1';
import { MenuStoreContextType } from '@gimlite/ui-v1/stores/menu.store';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OspFull from './assets/logos/osp_full';
import LogoPoolOSP from './assets/logos/osp_pool';
import { Query, QueryParkingsArgs } from './client/graphql';
import { parkingsGql } from './common/gql/parkings.gql';
import { RunWizard } from './components/run-wizard/run-wizard.component';
import { SearchBar } from './components/searchbar/searchbar.component';
import { config } from './config';

export const MainUse = observer(() => {
  const { SSOStore, WizardStore, MenuStore } = useStoreUIV1();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, lang } = useTranslation();

  const me = SSOStore.user as Query['me'] | null;

  const parkingsState = useQuery<
    { parkings: Query['parkings'] },
    QueryParkingsArgs
  >(parkingsGql, { variables: { limit: 300 } });

  const parkings = useMemo(() => {
    return parkingsState.data?.parkings?.list;
  }, [parkingsState.data]);

  const logos = useMemo(() => {
    switch (config.PORTAL) {
      case 'tenant':
        return LogoPoolOSP(t);
      case 'operator':
        if (me && me?.data?.operator?.display) {
          const logos: React.ReactNode[] = [];
          const display = me.data.operator.display;

          if (display.logoSecondary) {
            logos.push(
              <img
                key={`secondary`}
                style={{ height: '1.56rem' }}
                src={display.logoSecondary}
                alt="Reseller logo"
              />,
            );
          }

          if (display.logoMain) {
            logos.push(
              <img
                key={`primary`}
                style={{
                  maxHeight: '6.25rem',
                  height: '100%',
                  maxWidth: '80%',
                }}
                src={display.logoMain}
                alt="Operator logo"
              />,
            );
          }

          return logos;
        }

        return [];
      default:
        return [];
    }
  }, [me, config.PORTAL, lang]);

  useEffect(() => {
    if (parkings) {
      MenuStore.setMenu([
        {
          key: 'parkings',
          position: 'top',
          title: t('parkings'),
          items: parkings.map((p) => {
            const items: MenuStoreContextType['menu'][0]['items'] = [];

            items.push({
              key: `/${p._id}/dashboard`,
              label: t('dashboard'),
              icon: 'faHomeSolid',
            });

            items.push({
              key: `/${p._id}/sessions`,
              label: t('sessions'),
              icon: 'faClockSolid',
            });

            items.push({
              key: `/${p._id}/contracts`,
              label: t('contracts'),
              icon: 'faIdCardSolid',
              extra: (
                <RunWizard
                  handleEvent={{
                    click: () => {
                      WizardStore.select({
                        id: 'choiceCreateContract',
                        store: {
                          parkingId: p._id,
                          mode: config.PORTAL,
                          actions: {
                            createNewSingleContract:
                              config.PORTAL === 'operator',
                            createNewMasterPoolContract:
                              config.PORTAL === 'operator',
                            createNewSubPoolContract: true,
                            addOneMotoristToAnExistingPoolContract: true,
                            addSeveralMotoristsToAnExistingPoolContract: true,
                          },
                        },
                      });
                    },
                  }}
                />
              ),
            });

            if (config.PORTAL === 'operator') {
              items.push({
                key: `/${p._id}/breakdowns`,
                label: t('repairSessions'),
                icon: 'faTriangleExclamationSolid',
                extra: (
                  <RunWizard
                    handleEvent={{
                      click: () =>
                        WizardStore.select({
                          id: 'reportTechnicalDeviceFailure',
                          store: {
                            parkingId: p._id,
                          },
                        }),
                    }}
                  />
                ),
              });
            }

            items.push({
              key: `/${p._id}/incidents`,
              label: t('incidents'),
              icon: 'faCircleExclamationSolid',
            });

            if (config.PORTAL === 'operator') {
              items.push({
                key: `/${p._id}/products`,
                label: t('products'),
                icon: 'faBoxSolid',
              });
            }

            return {
              key: `/${p._id}/dashboard`,
              label: p.name,
              icon: 'faSquareParkingSolid',
              items: items,
            };
          }),
        },
      ]);
    }
  }, [parkings, lang]);

  useEffect(() => {
    if (pathname === '/' && parkings && parkings.length > 0) {
      navigate(`/${parkings[0]._id}/dashboard`);
    }
  }, [parkings]);

  return (
    <Main
      handleEvent={{
        selected: (key: string) => {
          navigate(key);
        },
      }}
      config={{
        navigation: {
          logo: logos,
          mode: 'drop',
        },
        header: {
          logo: OspFull(),
          extra: <SearchBar></SearchBar>,
        },
      }}
    />
  );
});
